import { type AccessRequestView, BREAK_GLASS_REASON_MAX_CHARS, CSRF_HEADER } from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleCheck, CircleSlash, Clock, Hourglass } from "lucide-react";
import { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { apiError, ok } from "../api-errors.ts";
import { formatDate } from "../i18n/format.ts";
import { describeChange } from "../projects/access-requests.ts";
import { navigate, projectHomePath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { useAdminApi } from "./admin-api.tsx";

/**
 * The acting admin's own access requests (openspec change `governance-access-approval`): the
 * changes that wait for a project's approval, and their outcome. Under a support grant the
 * operator has none: the query stays off.
 */
export const myAccessRequestsKey = ["admin", "access-requests"] as const;

export function useMyAccessRequests() {
  const { admin, readOnly } = useAdminApi();
  return useQuery({
    queryKey: myAccessRequestsKey,
    queryFn: async (): Promise<AccessRequestView[]> => ok(await admin["access-requests"].$get()),
    enabled: !readOnly,
    retry: false,
  });
}

/** Pending requests of one project, for the governed list's marker and the detail's notice. */
export function pendingFor(requests: readonly AccessRequestView[] | undefined, projectId: string) {
  return (requests ?? []).filter((r) => r.projectId === projectId && r.status === "pending");
}

const STATUS_ICON = {
  pending: Hourglass,
  approved: CircleCheck,
  rejected: CircleSlash,
  expired: Clock,
  cancelled: CircleSlash,
} as const;

/**
 * "Pending owner approval" on a governed project: what waits, until when, an optional reason for
 * the deciders, and break-glass as the way to read it now.
 */
export function PendingApprovalNotice(props: { projectId: string; projectName: string }) {
  const { t } = useTranslation();
  const mine = useMyAccessRequests();
  const [request] = pendingFor(mine.data, props.projectId);
  if (!request) {
    return null;
  }
  const others = request.group.size - 1;
  return (
    <Notice
      tone="info"
      live
      title={t("governance.pending.title")}
      action={<BreakGlassAction projectId={props.projectId} projectName={props.projectName} />}
    >
      <p>
        {t("governance.pending.detail", {
          change: describeChange(request, t),
          date: formatDate(request.expiresAt),
        })}{" "}
        {others > 0 ? t("governance.pending.group", { count: others }) : null}
      </p>
      <ReasonField key={request.id} request={request} />
    </Notice>
  );
}

function ReasonField({ request }: { request: AccessRequestView }) {
  const { t } = useTranslation();
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const id = useId();
  const [reason, setReason] = useState(request.reason ?? "");
  const save = useMutation({
    mutationFn: async () =>
      ok(
        await admin["access-requests"][":changeId"].reason.$put(
          { param: { changeId: request.changeId }, json: { reason } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: myAccessRequestsKey }),
  });
  return (
    <form
      className="mt-2 flex max-w-prose flex-col gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Label htmlFor={id} className="text-fg">
        {t("governance.pending.reasonLabel")}
      </Label>
      <Textarea
        id={id}
        value={reason}
        maxLength={500}
        rows={2}
        className="bg-surface-raised text-fg"
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="submit"
          size="sm"
          variant="outline"
          disabled={save.isPending || reason.trim() === "" || reason === (request.reason ?? "")}
        >
          {t("governance.pending.reasonSave")}
        </Button>
        <span role="status" className="text-xs text-fg-secondary">
          {save.isSuccess ? t("governance.pending.reasonSaved") : null}
        </span>
      </div>
      {save.error ? <p className="text-sm text-danger-fg">{save.error.message}</p> : null}
    </form>
  );
}

/** Audited break-glass: a reason, then the project opens as a Reader. */
function BreakGlassAction(props: { projectId: string; projectName: string }) {
  const { t } = useTranslation();
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const trigger = useRef<HTMLButtonElement>(null);
  const id = useId();
  const enter = useMutation({
    mutationFn: async () => {
      const res = await admin.projects[":projectId"]["break-glass"].$post(
        { param: { projectId: props.projectId }, json: { reason } },
        { headers: { [CSRF_HEADER]: csrfToken } },
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSuccess: () => navigate(projectHomePath(props.projectId)),
  });
  const close = () => {
    if (!enter.isPending) {
      setOpen(false);
      setReason("");
      enter.reset();
    }
  };
  return (
    <>
      <Button
        ref={trigger}
        size="sm"
        variant="ghost"
        className="-ml-2"
        onClick={() => setOpen(true)}
      >
        {t("governance.pending.breakGlass")}
      </Button>
      <AppDialog
        open={open}
        onClose={close}
        title={t("governance.pending.breakGlassTitle", { name: props.projectName })}
        description={t("governance.pending.breakGlassHelp")}
        returnFocus={trigger.current}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            enter.mutate();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={id}>{t("governance.pending.breakGlassReason")}</Label>
            <Textarea
              id={id}
              required
              rows={3}
              maxLength={BREAK_GLASS_REASON_MAX_CHARS}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          {enter.error ? <Notice tone="danger">{enter.error.message}</Notice> : null}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={enter.isPending} onClick={close}>
              {t("app.cancel")}
            </Button>
            <Button type="submit" disabled={enter.isPending || reason.trim() === ""}>
              {t("governance.pending.breakGlassConfirm")}
            </Button>
          </DialogFooter>
        </form>
      </AppDialog>
    </>
  );
}

/** "Your access requests": every request of the last 30 days, with its outcome. */
export function MyAccessRequests() {
  const { t } = useTranslation();
  const mine = useMyAccessRequests();
  if (!mine.data || mine.data.length === 0) {
    return null;
  }
  return (
    <section
      data-esi-content="projects"
      aria-labelledby="my-access-requests"
      className="flex flex-col gap-2"
    >
      <div className="flex flex-col gap-0.5">
        <h2 id="my-access-requests" className="text-base font-semibold text-fg">
          {t("governance.mine.title")}
        </h2>
        <p className="text-sm text-fg-secondary">{t("governance.mine.lead")}</p>
      </div>
      <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
        {mine.data.map((r) => {
          const Icon = STATUS_ICON[r.status];
          return (
            <li
              key={r.id}
              className="flex flex-wrap items-start justify-between gap-x-6 gap-y-1 px-4 py-3"
            >
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-sm font-medium text-fg">
                  {r.projectName ?? t("governance.mine.personal")}
                </span>
                <span className="text-sm text-fg-secondary">{describeChange(r, t)}</span>
                <span className="text-xs text-fg-muted tabular-nums">
                  {r.decidedAt
                    ? t("governance.mine.decided", { date: formatDate(r.decidedAt) })
                    : t("governance.mine.requested", { date: formatDate(r.createdAt) })}
                </span>
              </div>
              <span className="inline-flex items-center gap-1.5 text-sm text-fg-secondary">
                <Icon aria-hidden="true" className="size-4" strokeWidth={1.75} />
                {t(`governance.mine.status.${r.status}`)}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
